import type { PostHog } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';

// gloss
const flagListeners = new Map<string, Set<() => void>>();
const flagsRegistered = new Set<string>();

// gloss
const readFlag = (posthog: PostHog, flagKey: string, isFresh: boolean) =>
  isFresh ? posthog.getFeatureFlag(flagKey, { fresh: true }) : posthog.getFeatureFlag(flagKey);

// gloss
const resolveFlagValue = (posthog: PostHog, flagKey: string, defaultValue: boolean, isFresh: boolean): boolean => {
  const raw = readFlag(posthog, flagKey, isFresh);
  if (raw === undefined) return defaultValue;
  // why: An explicit PostHog false must always win over a caller's fail-open default — otherwise
  // why: a kill switch can't disable a default-on feature.
  return raw !== false;
};

export const clearFeatureFlagRegistry = () => {
  flagsRegistered.clear();
  flagListeners.clear();
};

// gloss
type FeatureFlagOptions = {
  timeoutMs?: number;
  defaultValue?: boolean;
};

// gloss
type FeatureFlagResult = {
  isEnabled: boolean;
  isLoading: boolean;
  hasTimedOut: boolean;
};

// gloss
export const useFeatureFlag = (
  flagKey: string,
  { timeoutMs, defaultValue = false }: FeatureFlagOptions = {},
): FeatureFlagResult => {
  const posthog = usePostHog();
  const [isEnabled, setIsEnabled] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const shouldWaitForLoad = timeoutMs !== undefined;

  const callback = useCallback((value: boolean) => {
    setIsEnabled(value);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!posthog) return;

    if (!flagsRegistered.has(flagKey)) {
      flagsRegistered.add(flagKey);
      flagListeners.set(flagKey, new Set());

      posthog.onFeatureFlags(() => {
        flagListeners.get(flagKey)?.forEach((notify) => {
          notify();
        });
      });
    }

    const notify = () => callback(resolveFlagValue(posthog, flagKey, defaultValue, shouldWaitForLoad));
    flagListeners.get(flagKey)?.add(notify);

    const rawInitial = readFlag(posthog, flagKey, shouldWaitForLoad);
    if (!shouldWaitForLoad) {
      callback(rawInitial === undefined ? defaultValue : rawInitial !== false);
    } else if (rawInitial !== undefined) {
      callback(rawInitial !== false);
    } else {
      setIsLoading(true);
    }
    setHasTimedOut(false);

    return () => {
      flagListeners.get(flagKey)?.delete(notify);
    };
  }, [posthog, flagKey, callback, shouldWaitForLoad, defaultValue]);

  useEffect(() => {
    if (!shouldWaitForLoad || !isLoading) return;
    const timer = setTimeout(() => {
      setHasTimedOut(true);
      setIsLoading(false);
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [shouldWaitForLoad, timeoutMs, isLoading, flagKey]);

  return { isEnabled, isLoading, hasTimedOut };
};
